import React, { useState, useRef } from 'react';
import ActionMenu from 'src/components/layouts/ActionMenu/ActionMenu';
import classNames from 'classnames';
import { useOutsideClick } from 'src/hooks/utils';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from '../Icon/Icon';
import Badge, { BadgeType } from '../Badge/Badge';
import styles from './ReportCard.module.scss';
import { OptionItemProps } from '../OptionItem/OptionItem';
import { useUpdateReportType } from '../../../services/reports';

export type ReportCardProps = {
  headline: string;
  description: string;
  icon: IconName;
  autoGenerated?: boolean;
  isFavorite?: boolean;
  category: string;
  badgeType: BadgeType;
  timePeriod?: string;
  id: number;
  allowActions?: boolean;
};

const ReportCard: React.FC<ReportCardProps> = ({
  category,
  headline,
  description,
  autoGenerated,
  icon,
  isFavorite = false,
  badgeType,
  timePeriod,
  id,
  allowActions = true,
}) => {
  const { t } = useTranslation();
  const [favorite, setFavorite] = useState(isFavorite);
  const [menuVisible, setMenuVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const updateFavorite = useUpdateReportType();

  const toggleFavorite = async (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await updateFavorite.mutateAsync(
        {
          params: {
            path: {
              id,
            },
          },
        },

        {
          onSuccess: () => {
            setFavorite(prevFavorite => !prevFavorite);
          },
        },
      );
    } catch (error) {
      console.error('Error updating favorite status:', error);
    }
  };

  const items = [
    {
      accessoryItem: 'download',
      label: t('reports.download'),
      onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        event.preventDefault();
        console.log('Download clicked');
        setMenuVisible(false);
      },
    },
    {
      accessoryItem: favorite ? 'starFilled' : 'star',
      label: favorite ? t('reports.remove_from_favorites') : t('reports.add_to_favorites'),
      onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
        toggleFavorite(event);
        setMenuVisible(false);
      },
    },
  ] as OptionItemProps[];

  const toggleMenu = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuVisible(prevVisible => !prevVisible);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(false);
    }
  };

  useOutsideClick(menuRef, handleClickOutside);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={classNames(styles.head)}>
          <div className={styles.icon}>
            <Icon className="text-inverted-full" name={icon} width={24} height={24} />
          </div>
        </div>
        <div className="flex flex-col px-20 pb-36">
          {autoGenerated && (
            <div className="flex flex-row items-center gap-x-2 mb-8">
              <Icon width={16} height={16} name="lightning" />
              <span className="typo-text-12--bold status-warning-full">{t('reports.auto_generated')}</span>
            </div>
          )}
          <h4 className="typo-text-16--bold text-full mb-8">{headline}</h4>
          <span className="typo-text-14 text-faded text-wrap truncate">{description}</span>
        </div>
        <div className={styles.meta}>
          <div>
            <Badge icon={icon} expanded label={category} type={badgeType} showBg={false} iconFirst />
          </div>
          <div className="typo-text-12 text-faded">{timePeriod}</div>
        </div>
      </div>
      {menuVisible && (
        <div className={styles.actionMenu} ref={menuRef}>
          <ActionMenu items={items} displayDivider={false} />
        </div>
      )}
      <div className={styles.actions}>
        <div onClick={event => toggleFavorite(event)} role="presentation" onKeyDown={event => toggleFavorite(event)}>
          {favorite ? (
            <Icon width={20} height={20} name="starFilled" className="text-full" />
          ) : (
            <Icon width={20} height={20} name="star" className="text-full opacity-60" />
          )}
        </div>
        {allowActions && (
          <div ref={buttonRef} onClick={toggleMenu} role="presentation" onKeyDown={toggleMenu}>
            <Icon name="settings" className={`${menuVisible ? 'settingsOpen' : 'text-faded'} cursor-pointer`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCard;
